/* eslint-disable spire/fenced-imports */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip from "@insite/mobius/Tooltip";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ErrorContext } from "@tKgpco/Overrides/Pages/CheckoutReviewAndSubmitPage";
import { shippingAddressStyles } from "@tKgpco/Overrides/Widgets/CheckoutShipping/ShippingAddress";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setAccountNumber from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/SetAccountHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const reduxAccountNumber = state.KGPState.CheckoutReview.accountNumber;
    const cartState: any = getCurrentCartState(state).value;
    const billTo = getBillToState(state, session.billToId).value;
    const isFreight = cartState?.properties?.doesCarrierSupportSupportFreight;
    const collectAccountNumber = cartState?.properties?.collectAccount || "";
    const service: any = cartState?.carrier?.description || "";
    const serviceType: any = cartState?.properties?.carrierServiceType || "";
    const deliveryTerms: any = billTo?.properties?.deliveryTerms || "";
    const accountNumber = "Account Number*";
    let accountLabel = `${service} ${accountNumber}`;

    const isVisble = deliveryTerms.toLowerCase() === "n" || deliveryTerms.toLowerCase() === "col";

    if (isFreight?.toLowerCase() === "true") {
        if (serviceType !== "" && serviceType === "Parcel") {
            accountLabel = `${service} ${siteMessage("Courier")} ${accountNumber}`;
        }
        if (serviceType !== "" && serviceType === "Freight") {
            accountLabel = `${service} ${siteMessage("Freight")} ${accountNumber}`;
        }
    }

    return {
        cartState,
        accountLabel,
        service,
        isVisble,
        reduxAccountNumber,
    };
};

const mapDispatchToProps = {
    setAccountNumber,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    accountGrid?: GridItemProps;
    accountWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    collectVal?: InjectableCss;
    errorText?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    accountGrid: {
        width: [12, 6, 6, 6, 6],
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    accountWrapper: {
        css: css`
            position: relative;
            width: 100%;
            display: flex;
            & > :nth-child(2) {
                width: 100%;
            }
            .collectAccountErrorStyle {
                input[type="text"] {
                    border: 2px solid ${COLORS.error};
                }
            }
        `,
    },
    infoWrapper: {
        css: css`
            position: absolute;
            right: 0.3rem;
        `,
    },
    errorText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${COLORS.error};
        `,
    },
    collectVal: {
        css: css`
            display: none;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const ShipToAccountNumber: FC<Props> = ({
    setAccountNumber,
    cartState,
    accountLabel,
    service,
    isVisble,
    reduxAccountNumber,
}) => {
    const errorRef: any = useRef<HTMLInputElement>(null);

    const { state, updateError } = useContext(ErrorContext);

    const collectAccountErrors: boolean = state?.collectAccountErrors;
    const collectAccountError = collectAccountErrors === true ? `collectAccountErrorStyle` : "";

    const shippingCollectAccountNumber = cartState?.properties?.shippingCollectAccountNumber || "";

    const collectAccountNum = cartState?.properties?.collectAccount || "";

    const awaitingAapproval = cartState?.isAwaitingApproval;

    useEffect(() => {
        if (collectAccountNum && !awaitingAapproval) {
            setAccountNumber({ accountNumber: collectAccountNum });
            updateError("collectAccountErrors", false);
        } else if (shippingCollectAccountNumber) {
            setAccountNumber({ accountNumber: shippingCollectAccountNumber });
            updateError("collectAccountErrors", false);
        } else {
            setAccountNumber({ accountNumber: "" });
        }
    }, [cartState]);

    useEffect(() => {
        if (collectAccountErrors === true) {
            errorRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, [collectAccountErrors]);

    const editAccountNumber = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value) {
            updateError("collectAccountErrors", false);
        } else {
            updateError("collectAccountErrors", true);
        }
        setAccountNumber({ accountNumber: e.currentTarget.value });
    };

    const getAccount = () => {
        return (
            <>
                <TextField
                    ref={errorRef}
                    label={accountLabel}
                    value={reduxAccountNumber}
                    onChange={e => {
                        editAccountNumber(e);
                    }}
                />
            </>
        );
    };

    return (
        <>
            {isVisble ? (
                <GridContainer>
                    {/* Custom Account Field Added -- start */}
                    <GridItem {...styles.accountGrid}>
                        <StyledWrapper {...styles.accountWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Tooltip
                                    {...shippingAddressStyles.shipTooltip}
                                    className="toolTip-wrapper"
                                    text={siteMessage("Carrier_Account_Number_Info") as string}
                                />
                            </StyledWrapper>
                            <StyledWrapper className={collectAccountError}>
                                {getAccount()}
                                {collectAccountError && (
                                    <StyledWrapper {...styles.errorText}>
                                        {siteMessage("Account_required")}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                    {/* Custom Account Field Added -- End */}
                </GridContainer>
            ) : null}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipToAccountNumber),
    definition: {
        displayName: "Account Number",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
